// src/components/StrategyBuilder/StepConditions.jsx
import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stack, 
  Alert, 
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon,
  InfoOutlined as InfoIcon,
  TipsAndUpdates as TipsIcon,
} from '@mui/icons-material';
import ConditionCard from './ConditionCard';

const StepConditions = ({ data, onChange }) => {
  const addCondition = () => {
    const newCondition = {
      id: Date.now(),
      name: `Condition ${(data.conditions || []).length + 1}`,
      type: '',
      signals: [],
      trigger: null,  // Changed from trigger_signal
      target: null,   // Changed from target_signal
      interference_signals: [],
      timing: { min_minutes: 0, min_seconds: 0, max_minutes: 0, max_seconds: 0 },
    };
    onChange({ ...data, conditions: [...(data.conditions || []), newCondition] });
  };

  const updateCondition = (index, updatedCondition) => {
    const newConditions = [...(data.conditions || [])];
    newConditions[index] = updatedCondition;
    onChange({ ...data, conditions: newConditions });
  };

  const deleteCondition = (index) => {
    onChange({
      ...data,
      conditions: (data.conditions || []).filter((_, i) => i !== index)
    });
  };

  const hasEnoughSignals = data.expected_signals && data.expected_signals.length >= 2;
  const conditionCount = (data.conditions || []).length;

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TipsIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800, 
                color: '#1e293b',
                mb: 0.5,
              }}
            >
              Configure Trigger Conditions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define the logical conditions that must be met for your strategy to trigger
            </Typography>
          </Box>
          <Chip
            label={`${conditionCount} Condition${conditionCount !== 1 ? 's' : ''}`}
            sx={{
              bgcolor: conditionCount > 0 ? '#ede9fe' : '#f1f5f9',
              color: conditionCount > 0 ? '#7c3aed' : '#64748b',
              fontWeight: 700,
              fontSize: '0.875rem',
              height: 32,
              px: 1,
            }}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />
      </Box>

      {/* Info Alert */}
      <Alert 
        icon={<InfoIcon />}
        severity="info" 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          bgcolor: '#eff6ff',
          border: '1px solid #bfdbfe',
          '& .MuiAlert-icon': {
            color: '#3b82f6',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e3a8a' }}>
          Each condition acts as a trigger rule. Combine your signals using time bound, simultaneous, or interference patterns to create sophisticated trading logic.
        </Typography>
      </Alert>

      {/* Action Bar */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 4,
          borderRadius: 3,
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
              Condition Builder
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasEnoughSignals 
                ? 'Add conditions to build your strategy logic'
                : 'Please define at least 2 signals before creating conditions'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addCondition}
            disabled={!hasEnoughSignals}
            sx={{
              background: hasEnoughSignals 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : '#cbd5e1',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: 2,
              boxShadow: hasEnoughSignals 
                ? '0 4px 12px rgba(102, 126, 234, 0.25)'
                : 'none',
              '&:hover': {
                background: hasEnoughSignals
                  ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  : '#cbd5e1',
                boxShadow: hasEnoughSignals
                  ? '0 6px 16px rgba(102, 126, 234, 0.35)'
                  : 'none',
                transform: hasEnoughSignals ? 'translateY(-2px)' : 'none',
              },
              transition: 'all 0.2s',
              '&:disabled': {
                color: 'white',
              },
            }}
          >
            Add New Condition
          </Button>
        </Stack>
      </Paper>

      {/* Conditions List */}
      {conditionCount === 0 ? (
        <Paper
          sx={{
            p: 8,
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed #cbd5e1',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <TipsIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
          </Box>
          
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              color: '#1e293b',
              mb: 1,
            }}
          >
            No Conditions Yet
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
          >
            Start building your strategy logic by adding your first condition. Each condition defines when and how your signals should trigger.
          </Typography>
          
          {hasEnoughSignals && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={addCondition}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.35)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s',
              }}
            >
              Create First Condition
            </Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={3}>
          {(data.conditions || []).map((condition, index) => (
            <Box
              key={condition.id}
              sx={{
                position: 'relative',
                '&::before': index !== conditionCount - 1 ? {
                  content: '""',
                  position: 'absolute',
                  left: 23,
                  top: '100%',
                  width: 2,
                  height: 24,
                  background: 'linear-gradient(180deg, #cbd5e1 0%, transparent 100%)',
                  zIndex: 0,
                } : {},
              }}
            >
              <ConditionCard
                condition={condition}
                index={index}
                availableSignals={data.expected_signals || []}
                onUpdate={(updated) => updateCondition(index, updated)}
                onDelete={() => deleteCondition(index)}
              />
            </Box>
          ))}
        </Stack>
      )}

      {/* Helper Text */}
      {conditionCount > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 2,
            bgcolor: '#fef3c7',
            border: '1px solid #fde68a',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <InfoIcon sx={{ color: '#d97706', mt: 0.3 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                Pro Tip
              </Typography>
              <Typography variant="body2" sx={{ color: '#78350f' }}>
                All conditions must be satisfied for the strategy to trigger. You'll select a final confirmation condition in the next step.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default StepConditions;
